import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Activity } from "lucide-react";

const rel = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s ago`;
};

export default function ActivityFeed({ projectId, status }) {
  const [log, setLog] = useState([]);
  const [now, setNow] = useState(Date.now());
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;
    const load = () =>
      api.get(`/projects/${projectId}/activity`).then((r) => {
        if (active) setLog(r.data.log || []);
      }).catch(() => {});
    load();
    const t = setInterval(load, 2500);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      active = false;
      clearInterval(t);
      clearInterval(tick);
    };
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [log]);

  const running = status === "running";
  const latest = log[log.length - 1];

  return (
    <div className="mt-6" data-testid="activity-feed">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex items-center justify-center">
          <Activity className={`w-3.5 h-3.5 ${running ? "text-amber-400" : "text-zinc-600"}`} />
          {running && <span className="absolute w-3.5 h-3.5 rounded-full bg-amber-500/30 blur-sm pulse-glow" />}
        </span>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Live Activity
        </span>
        {running && latest && (
          <span className="ml-auto font-mono2 text-[10px] text-amber-400/80" key={now}>
            {rel(latest.t)}
          </span>
        )}
      </div>
      <div
        ref={scrollRef}
        className="max-h-56 overflow-y-auto space-y-2 border border-zinc-800 rounded-sm bg-zinc-950/60 p-3"
      >
        {log.length === 0 ? (
          <p className="text-xs text-zinc-600">No activity yet. Press Run pipeline.</p>
        ) : (
          log.map((l, i) => {
            const last = i === log.length - 1;
            return (
              <div key={i} className="flex items-start gap-2">
                <span
                  className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    last && running ? "bg-amber-400 pulse-glow" : "bg-zinc-700"
                  }`}
                />
                <div className="min-w-0">
                  <p className={`text-xs leading-snug ${last ? "text-zinc-200" : "text-zinc-500"}`}>
                    {l.msg}
                  </p>
                  <p className="font-mono2 text-[9px] text-zinc-600">{rel(l.t)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
